import React, { Component } from 'react';
import { func, string, bool, number, oneOf } from 'prop-types';

import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import copy from 'copy-to-clipboard';

import { Button, SnapaskLoader, GiftIcon } from '@snapask/common-components';

import {
  getProfileInfo,
  getIsPaidUser,
  getIsProfileCompleted,
  getReferralQuotaThreshold,
  getFetchProfileStatus,
} from '@dragonfruit/redux/profile';
import { showAlertBanner } from '@dragonfruit/redux/alertBanner';
import { redeemReferralCode } from '@dragonfruit/redux/redeemReferralCode';
import { account } from '@dragonfruit/redux/navigation';

import LineInput from '@dragonfruit/components/fields/LineInput';

import {
  ALERT_SUCCESS_THEME,
  ALERT_ERROR_THEME,
} from '@dragonfruit/constants/alertBannerThemes';
import { SHARE_REFERRAL_URL } from '@dragonfruit/constants/gtmEvents';
import * as LOADING_STATE from '@dragonfruit/constants/loadingState';

import Validator from '@dragonfruit/utils/validator';

import { sendGtmEvent } from '@dragonfruit/redux/googleTagManager';

import NeedCompletedProfileDialog from './components/NeedCompletedProfileDialog';

import styles from './AccountReferral.scss';
import CopyReferral from './components/CopyReferral/CopyReferral';
import SocialMedia from './components/SocialMedia/SocialMedia';

const mapStateToProps = state => ({
  userId: getProfileInfo(state, 'userId'),
  referralCode: getProfileInfo(state, 'referralCode'),
  isPaidUser: getIsPaidUser(state),
  isProfileCompleted: getIsProfileCompleted(state),
  referralQuotaThreshold: getReferralQuotaThreshold(state),
  profileStatus: getFetchProfileStatus(state),
});

class AccountReferral extends Component {
  static propTypes = {
    account: func.isRequired,
    isPaidUser: bool.isRequired,
    profileStatus: oneOf(Object.values(LOADING_STATE)).isRequired,
    redeemReferralCode: func.isRequired,
    sendGtmEvent: func.isRequired,
    showAlertBanner: func.isRequired,
    t: func.isRequired,
    isProfileCompleted: bool,
    referralCode: string,
    referralQuotaThreshold: number,
    userId: string,
  };

  static contextTypes = {
    region: string,
  };

  static defaultProps = {
    userId: '',
    isProfileCompleted: false,
    referralCode: '',
    referralQuotaThreshold: 0,
  };

  state = {
    redeemCode: '',
    redeemCodeTouched: false,
    redeemCodeEmpty: true,
    showNeedCompletedProfile: false,
  };

  onSubmitRedeemReferralCode = event => {
    event.preventDefault();

    const { region } = this.context;
    const { redeemReferralCode, userId } = this.props;
    const { redeemCode: code } = this.state;
    redeemReferralCode({
      userId,
      code,
      region,
    });
  };

  onClickFacebookShare = () => {
    const { t, referralCode, sendGtmEvent } = this.props;

    if (this.handleShowNeedCompletedProfile()) {
      return;
    }

    window.FB.ui(
      {
        method: 'share',
        quote: `${t('account.shareContent')} ${referralCode}`,
        href: t('account.shareUrl'),
      },
      response => {
        if (response) {
          sendGtmEvent({ eventName: SHARE_REFERRAL_URL });

          showAlertBanner({
            theme: ALERT_SUCCESS_THEME,
            message: t('account.facebookShareSuccess'),
          });

          return;
        }
        showAlertBanner({
          theme: ALERT_ERROR_THEME,
          message: t('account.facebookShareError'),
        });
      }
    );
  };

  onChangeRedeemCode = value => {
    this.setState({
      redeemCode: value,
      redeemCodeTouched: true,
    });
  };

  onClickCopy = () => {
    const { referralCode, showAlertBanner, t } = this.props;

    if (this.handleShowNeedCompletedProfile()) {
      return;
    }

    const result = copy(referralCode);
    const alertBanner = result
      ? {
          theme: ALERT_SUCCESS_THEME,
          message: t('account.referralCopySuccess'),
        }
      : {
          theme: ALERT_ERROR_THEME,
          message: t('account.referralCopyError'),
        };

    showAlertBanner(alertBanner);
  };

  onClickLater = () => {
    this.setState({
      showNeedCompletedProfile: false,
    });
  };

  onClickUpdateProfile = () => {
    this.setState({
      showNeedCompletedProfile: false,
    });
    this.props.account();
  };

  onClickSocialMedia = event => {
    const { sendGtmEvent } = this.props;

    if (this.handleShowNeedCompletedProfile()) {
      event.preventDefault();
    }

    sendGtmEvent({ eventName: SHARE_REFERRAL_URL });
  };

  handleShowNeedCompletedProfile() {
    const { isPaidUser, isProfileCompleted } = this.props;
    // free user who does not complete profile, can't copy referral
    if (isPaidUser || isProfileCompleted) {
      return false;
    }

    this.setState({
      showNeedCompletedProfile: true,
    });

    return true;
  }

  validateRedeemCode = () => {
    const validate = new Validator(this.state.redeemCode).isEmpty();

    this.setState({
      redeemCodeEmpty: validate.haveError(),
    });
  };

  render() {
    const {
      t,
      referralCode,
      isPaidUser,
      referralQuotaThreshold,
      profileStatus,
    } = this.props;
    const {
      redeemCodeEmpty,
      redeemCodeTouched,
      showNeedCompletedProfile,
    } = this.state;

    const referralString = isPaidUser
      ? 'account.referralPaid'
      : 'account.referral';

    if (profileStatus !== LOADING_STATE.READY) {
      return <SnapaskLoader />;
    }

    return (
      <div className={styles.cardContainer}>
        {showNeedCompletedProfile && (
          <NeedCompletedProfileDialog
            onClickLater={this.onClickLater}
            onClickUpdateProfile={this.onClickUpdateProfile}
          />
        )}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.text}>
              <GiftIcon width={40} height={40} />
              <h3>{t(`${referralString}Title`)}</h3>
            </div>
          </div>
          <div className={styles.body}>
            <p>
              {t(`${referralString}Text`, {
                count: referralQuotaThreshold,
              })}
            </p>

            <div className={styles.contentContainer}>
              <div className={styles.inputContainer}>
                <CopyReferral
                  btnText={t('account.copyReferral')}
                  onClickCopy={this.onClickCopy}
                  referralCode={referralCode}
                />
                <SocialMedia
                  onClickFacebookShare={this.onClickFacebookShare}
                  onClickSocialMedia={this.onClickSocialMedia}
                />

                {/* Redeem Referral Code */}

                <h3 className={styles.title}>
                  {t('account.redeemReferralCodeTitle')}
                </h3>
                <form
                  className={styles.userCode}
                  onSubmit={this.onSubmitRedeemReferralCode}
                >
                  <div className={styles.input}>
                    <LineInput
                      type="text"
                      onChange={this.onChangeRedeemCode}
                      onBlur={this.validateRedeemCode}
                      floatingLabelText={t('account.redeemCode')}
                      errorText={
                        redeemCodeTouched &&
                        redeemCodeEmpty &&
                        t('account.redeemReferralErrors.noEmpty')
                      }
                    />
                  </div>
                  <div className={styles.spacer} />
                  <div className={styles.btnContainer}>
                    <Button
                      type="submit"
                      text={t('account.redeemCodeCta')}
                      disabled={redeemCodeEmpty}
                      primary
                    />
                  </div>
                </form>
              </div>
              <div className={styles.spacer} />
              <div className={styles.imageContainer}>
                <img
                  src={t('account.referralImage')}
                  alt={t('account.referralTitle')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default translate(['account'])(
  connect(mapStateToProps, {
    redeemReferralCode,
    showAlertBanner,
    account,
    sendGtmEvent,
  })(AccountReferral)
);



// WEBPACK FOOTER //
// ./app/containers/Account/children/AccountReferral/AccountReferral.jsx